/**
 * Domain constants shared by API and web.
 * Single source of truth for every enum the PRD defines.
 */

/**
 * LEAD-002 — inquiry status flow.
 *
 * These name what the owner of the business actually did about the enquiry,
 * which is what the list is filtered by. The set they replace — new,
 * in_progress, converted, closed, not_relevant — named where the record was in
 * a pipeline instead, and two of its five values ("converted", "not relevant")
 * meant nothing on a screen where every row is a person who rang up.
 *
 * `closed` covers both endings, wanted and unwanted. Splitting them told the
 * list nothing it does not already show: a closed enquiry that turned into work
 * has a quote attached to it and an unwanted one does not.
 */
export const INQUIRY_STATUSES = ['pending', 'meeting_scheduled', 'quote_requested', 'closed'] as const;

/**
 * Legal status transitions (LEAD-002: "no illegal jumps").
 *
 * The three live states reach each other freely — asking for a price and then
 * booking a meeting is as ordinary as the reverse — and `closed` is the end of
 * every one of them. Reopening lands back on `pending`, because an enquiry that
 * is open again is by definition waiting to be dealt with.
 */
export const INQUIRY_TRANSITIONS: Record<string, readonly string[]> = {
  pending: ['meeting_scheduled', 'quote_requested', 'closed'],
  meeting_scheduled: ['pending', 'quote_requested', 'closed'],
  quote_requested: ['pending', 'meeting_scheduled', 'closed'],
  closed: ['pending'],
};

/**
 * What the main filter offers, in its order. Not simply `INQUIRY_STATUSES`:
 * the filter leads with "all", and `closed` is deliberately not one of its
 * entries — a finished enquiry is reached through "all", where its own badge
 * still says so, rather than through a filter chip nobody sets out to press.
 */
export const INQUIRY_FILTER_STATUSES = ['pending', 'meeting_scheduled', 'quote_requested'] as const;

/**
 * LEAD-009 — the voice an AI phone agent answers in. Two entries and not a
 * catalogue of named voices: the provider's voice list changes under us, and
 * the only part of it a business ever picks is which one it sounds like.
 */
export const AGENT_VOICES = ['female', 'male'] as const;

/** LEAD-006 — origin channel. */
export const INQUIRY_SOURCES = ['manual', 'telephony', 'mini_site', 'integration'] as const;

/**
 * LEAD-004 — what a quote or an inquiry should do about a phone number that is
 * already on a customer card.
 *
 * Three of them are answers a person gave on screen: `keep` leaves the card as
 * it stands and fills in only the fields it is still missing, `update` lets the
 * details just typed win, and `new` opens a second card for a number two people
 * genuinely share. `auto` is the absence of an answer — the mini-site form, an
 * older client, or a form where nothing disagreed — and the server reads it as
 * `keep` on somebody else's card and as `update` on the one the document being
 * edited already sits on.
 */
export const CUSTOMER_MATCH_MODES = ['auto', 'keep', 'update', 'new'] as const;

/**
 * QUO — quote lifecycle.
 * `sent` is the "pending / awaiting customer response" state; the Sent KPI counts
 * every quote that has ever been sent (sent + approved + rejected + expired).
 */
export const QUOTE_STATUSES = ['draft', 'sent', 'approved', 'rejected', 'expired'] as const;

/**
 * QUO-018 / HIST — the send history's tabs, in the order they are offered.
 *
 * They are named for quote statuses and read on both halves of the merged
 * history, because a document for signature passes through the same few states
 * under different names: a document the customer has opened is "viewed" exactly
 * as an opened quote is, and a signed one is the ending an approved quote has.
 *
 * The order is the order of a job rather than the order of the lifecycle
 * enum — what is waiting on somebody first, what has ended after it, and the
 * drafts nobody outside has seen last.
 *
 * `sent` used to stand where `pending` does and meant "everything that ever
 * left the building", which is every row but the drafts — a tab that hid
 * nothing, next to a chip that said "נשלחו" and could not be told from a filter
 * someone had left applied. What the screen is actually read for is what is
 * still owed an answer, and that is what `pending` holds.
 */
export const QUOTE_TABS = [
  'all',
  'pending',
  'viewed',
  'expired',
  'rejected',
  'approved',
  'locked',
  'draft',
] as const;

/**
 * What the merged send history is allowed to show. `all` is the default and
 * the point of the screen: one list, both senders.
 */
export const HISTORY_KINDS = ['all', 'quote', 'signature', 'invoice-folder', 'library-folder'] as const;

/** QUO-016 — the 4 rejection reasons, fixed by the PRD. */
export const REJECTION_REASONS = ['price', 'better_offer', 'improve_terms', 'not_relevant'] as const;

/**
 * Translation keys for the rejection reasons. Never render these raw — pass
 * them through `t()` (see ./i18n). Same pattern for every other enum: the
 * dictionaries hold `enum.<group>.<value>` entries.
 */
export const REJECTION_REASON_KEYS: Record<(typeof REJECTION_REASONS)[number], string> = {
  price: 'enum.rejectionReason.price',
  better_offer: 'enum.rejectionReason.better_offer',
  improve_terms: 'enum.rejectionReason.improve_terms',
  not_relevant: 'enum.rejectionReason.not_relevant',
};

export const DISCOUNT_TYPES = ['none', 'amount', 'percent'] as const;

/** QUO-022 — a saved template holds either prose or a set of quote lines. */
/*
 * The fields a saved template can belong to.
 *
 * There used to be two: 'items' for a set of rows, and 'text' for everything
 * written in prose — which was three different fields sharing one drawer. A
 * clause saved from the terms box turned up in the discount label and in the
 * description of the work, so every list was mostly other fields' text and the
 * one template being looked for was somewhere in it.
 *
 * A template is only ever a starting point for the field it was written in, so
 * the kind is now that field. 'text' is kept because rows in the database carry
 * it: it reads as the description, which is where nearly all of them came from,
 * and nothing new is written under it.
 */
export const TEMPLATE_KINDS = ['service', 'legal', 'discount', 'items', 'text'] as const;

/** What a stored kind means today — 'text' predates the split. */
export const TEMPLATE_KIND_ALIASES: Record<string, string> = { text: 'service' };

/**
 * ADM-001 — whether Worki lets an account in at all.
 *
 * Separate from the subscription on purpose: `past_due` is a business that owes
 * money and still runs, while `suspended` is a door Worki closed. Conflating
 * the two would mean a failed charge locking someone out and a fraud case
 * getting a grace period.
 */
export const BUSINESS_ACCESS = ['active', 'pending', 'suspended'] as const;

/** ADM-006 — where a would-be customer has got to. */
export const PLATFORM_LEAD_STATUSES = ['new', 'contacted', 'converted', 'lost'] as const;

/** REM — reminder status. */
export const REMINDER_STATUSES = ['open', 'completed'] as const;

/** Modules a reminder can originate from (REM-001). */
export const REMINDER_SOURCES = ['manual', 'quote', 'inquiry', 'customer', 'call', 'signature'] as const;

/** DASH-003 — dashboard time ranges. */
export const TIME_RANGES = ['today', 'week', 'month', 'all'] as const;

/**
 * QUO-007 — how long the customer's link to a quote stays open, in days.
 *
 * A choice on the quote rather than one number for the business: "today only"
 * is a price that moves with the market and "thirty days" is a job being
 * planned, and the same business writes both in a week. `quoteLinkTtlDays`
 * stays the default for a quote that says nothing.
 */
export const QUOTE_VALID_DAYS = [1, 7, 14, 30] as const;

/*
 * QUO-012 — how long a quote or a document may go unanswered before Worki
 * writes the follow-up.
 *
 * `null` leads, and is not "never": it is the business's own threshold, the
 * answer for most of what goes out and the reason this is four buttons rather
 * than a number to type. The three that follow are the hours a sender actually
 * reaches for — same day, next day, the day after — and they stop there because
 * a chase further out than that is a reminder somebody writes themselves.
 */
export const QUOTE_FOLLOW_UP_HOURS = [null, 12, 24, 48] as const;

/*
 * SIG-004 — the same two choices, on a document sent for signature.
 *
 * Deliberately the same four windows and the same four chases rather than a
 * second set of numbers: a sender who has learnt what "48" means beside a quote
 * has learnt what it means beside a document, and the two screens sit one tab
 * apart. Aliases rather than copies, so they cannot drift into disagreeing.
 */
export const SIGNATURE_VALID_DAYS = QUOTE_VALID_DAYS;
export const SIGNATURE_FOLLOW_UP_HOURS = QUOTE_FOLLOW_UP_HOURS;

/*
 * INV-006 — the periods the invoice archive is read in.
 *
 * Calendar periods, not rolling ones. The archive is read to answer an
 * accounting question — what was spent this month, how much VAT comes back —
 * and an accounting month starts on the first, not thirty days ago. A rolling
 * window gave a total that no report, return or bank statement agreed with.
 */
export const INVOICE_RANGES = ['week', 'month', 'twoMonths', 'year', 'all'] as const;

/*
 * How many calendar years the invoice archive reaches: this one and the three
 * before it, from 1 January, moving on every new year.
 *
 * One number for the mailbox scan, the list, its totals and the year chips.
 * They used to be counted two ways — the scan four years of days back from
 * today, the chips four calendar years — so a first sync in October 2026 pulled
 * in an October 2022 invoice that no year chip could name, and it sat in the
 * list looking like a filter that had stopped working.
 *
 * A row older than the first of these years is hidden, not deleted: it stays in
 * the database, out of every list, total and selection.
 */
export const INVOICE_ARCHIVE_YEARS = 4;

/** The first day the invoice archive shows: 1 January, three years back, in local time. */
export function invoiceArchiveStart(now = new Date()): Date {
  return new Date(now.getFullYear() - (INVOICE_ARCHIVE_YEARS - 1), 0, 1);
}

/*
 * INV-007 — what an imported document says it is, in the order it is offered.
 *
 * The three that carry a right to deduct input VAT come first, because that is
 * the question the archive is opened to answer; the rest are documents a
 * business keeps and cannot reclaim against. `other` is last and means Worki
 * could not read a title, which is a different thing from the document not
 * having one.
 */
export const INVOICE_DOC_TYPES = [
  'tax_invoice',
  'tax_invoice_receipt',
  'en_tax_invoice',
  'receipt',
  'proforma',
  'credit_note',
  'not_yet_tax_invoice',
  'en_invoice',
  'other',
] as const;

/**
 * INV-007 — rows imported before the classifier ran, which store no type.
 *
 * Not a document type, so it is kept out of the list above and off the badges.
 * The filter still has to reach them: every invoice in the archive older than
 * the classifier stores no type at all. Since 1.5 they are not a choice of
 * their own but part of the "unrecognised" one, beside `other` (DOC_TYPE_OPTIONS
 * in InvoicesPage.tsx). The server still takes `unset` on its own, which is
 * what the installed app sends.
 */
export const INVOICE_DOC_TYPE_UNSET = 'unset';

/**
 * INV-010 — the scan failures only the mailbox's owner can fix, by logging in
 * to it again.
 *
 * A grant Google stopped honouring (the consent screen is still in testing, so
 * every grant dies after a week), an app password that was revoked or never
 * was one. None of them is fixed by syncing again, and before this list the
 * only way out on the screen was to disconnect the mailbox — which deletes
 * every invoice it ever brought in — and connect it from scratch.
 *
 * A scan that fails with one of these leaves the account in `reconnect`, the
 * status the screen reads to offer the login in place.
 */
export const MAILBOX_RECONNECT_ERRORS = [
  'invoices.oauthReconnectRequired',
  'invoices.imapAuthFailed',
  'invoices.imapAppPasswordRequired',
  'invoices.imapCredentialsRequired',
] as const;

export function needsMailboxLogin(errorCode: string | null | undefined): boolean {
  return errorCode != null && (MAILBOX_RECONNECT_ERRORS as readonly string[]).includes(errorCode);
}

/**
 * INV-006 — how an invoice reached the archive.
 *
 * `mail` is a scanned mailbox, which is where all of them used to come from.
 * `manual` is the counter: paper handed over, photographed or uploaded, with
 * the amount typed in — there is no text on a photograph to read one off.
 */
export const INVOICE_SOURCES = ['mail', 'manual'] as const;

/**
 * Section 5 (Nitzan, 05.10) — which way the money on an invoice went.
 *
 * `expense` is everything the archive held until now: a document a supplier
 * sent the business. `income` is one the business wrote itself — a builder or a
 * hardware shop with a paper invoice book — photographed so it is kept with the
 * rest. On an income row the other party is the customer, and its name is kept
 * where an expense keeps the supplier's (see `InvoiceRecord.supplier`).
 */
export const INVOICE_DIRECTIONS = ['expense', 'income'] as const;

/**
 * The file a business may put in as an invoice.
 *
 * Photographs as well as PDFs, because the invoice this exists for is a piece
 * of paper and the camera is what is to hand. A photograph is turned into a
 * one-page PDF on arrival: the preview, the download and the bundle sent to an
 * accountant all read PDFs, and a stored JPEG would break all three.
 *
 * JPEG and PNG and no other image, because those are the two pdfkit can embed
 * and there is no image library here to convert a third into them. Every phone
 * camera writes JPEG, so the list covers what this is actually for.
 */
export const INVOICE_UPLOAD_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;

/** QUO-010 — customer-side tracking events on the interactive link. */
export const QUOTE_EVENT_TYPES = ['opened', 'heartbeat', 'approve_started', 'approved', 'rejected'] as const;

/** Notification / real-time event channel names (INFRA-009). */
export const NOTIFICATION_TYPES = [
  'quote_opened',
  'quote_approved',
  'quote_rejected',
  'reminder_due',
  'inquiry_created',
] as const;

export const CALL_STATUSES = ['answered', 'missed', 'callback'] as const;
export const SUBSCRIPTION_PLANS = ['starter', 'business', 'pro'] as const;

/** The regions a business can say it works in, on its gallery page. Ids; the labels are miniSite.areas.* in i18n. */
export const SERVICE_AREAS = ['north', 'haifa', 'sharon', 'center', 'jerusalem', 'shfela', 'south', 'eilat'] as const;

/**
 * Israel's standard VAT rate, in percent — 18% since 1 Jan 2025. It is the
 * default for a new business, never a hardcoded constant in the arithmetic:
 * each business stores its own rate and each quote snapshots the rate it was
 * issued under, so a future rate change cannot rewrite past documents.
 */
export const DEFAULT_VAT_RATE = 18;

/**
 * ADM-011 — how many days before a subscription runs out the back office is
 * told about it.
 *
 * Four is the shipped answer, not the only one: a platform that renews by hand
 * wants longer, one that renews by card wants barely any, and which it is
 * changes as the business does. The number is therefore an admin setting and
 * this is what it starts at.
 */
export const DEFAULT_SUBSCRIPTION_ALERT_DAYS = 4;

/** Hard business limits. */
export const LIMITS = {
  /** VOICE-001 — the recorder stops itself here; the server refuses longer. */
  maxVoiceSeconds: 120,
  /** 120 s of opus is about 1 MB; five leaves room for a phone's own codec. */
  maxVoiceBytes: 5 * 1024 * 1024,
  /** Jobs waiting host-wide before an upload is refused rather than queued. */
  voiceQueueCap: 10,
  /** A job still running after this is failed by the runner. */
  voiceJobTimeoutMs: 180_000,
  /** Transcripts and proposals are cleared after this; the counters stay. */
  voiceRetentionDays: 90,
  /** QUO-009 — interactive link lifetime. */
  quoteLinkTtlDays: 7,
  /** QUO-013 — approval code lifetime once displayed. */
  approvalCodeTtlMinutes: 15,
  /** DASH-008 — activity feed size on the dashboard. */
  dashboardActivityCount: 6,
  /** INV — max connected email accounts (Phase 3, enforced from day one in schema). */
  maxConnectedEmailAccounts: 2,
  /**
   * INV-006 — an invoice uploaded or photographed rather than synced.
   *
   * Sized for a phone camera rather than for a document: a modern handset
   * writes 4-8MB a frame, and a cap that a photograph of a receipt trips is a
   * cap that rejects the only thing this exists for.
   */
  maxInvoiceUploadBytes: 15 * 1024 * 1024,
  /**
   * SITE — mini-site caps (Phase 4). `maxGalleries` is a ceiling over the plan
   * table rather than a rule of its own: `planLimits()` applies it, so a plan
   * can be stricter but none can be looser.
   */
  maxGalleries: 20,
  maxImagesPerGallery: 10,
  /**
   * INV-006 — how many invoices one bulk action may name.
   *
   * The binding one is sharing: the selection is merged into a single PDF held
   * in memory, so this is the number of documents the server opens at once, not
   * a count of rows. Filing and deleting are cheap and share the ceiling anyway
   * — one cap the screen can explain beats three the user has to discover.
   */
  maxBulkInvoices: 200,
  /**
   * SIG — how long a signing link stays open. Longer than a quote's: a quote
   * expires because a price does, and a consent form has no price to go stale.
   */
  signatureLinkTtlDays: 30,
  /** SIG — the drawn signature, as PNG bytes. A few strokes; a photo is not one. */
  maxSignatureBytes: 512 * 1024,
  /**
   * SIG — a signature drawn into the body of a digital document, as PNG bytes.
   *
   * Smaller than the closing signature above, and deliberately so. These travel
   * as answers inside the same JSON request, several at a time, through a body
   * parser that stops at a megabyte — a limit generous for a few strokes on a
   * transparent canvas (tens of kilobytes) and short of a pasted photograph.
   */
  maxDocumentSignatureBytes: 64 * 1024,
  /**
   * 1.1 — the sender's own signature, drawn on the compose screen and carried on
   * each box it is printed in, as PNG bytes.
   *
   * Smaller again, for the same parser: the whole placement is saved as one JSON
   * body each time the sender changes it, and a document may hold twenty boxes.
   * Twenty at this size come to about 880KB once base64 has grown them by a
   * third, inside the megabyte; at 48KB they came to 1.3MB and the save was
   * refused. The screen scales the drawing down to 600 pixels across before it
   * is kept, and narrower when that still does not fit — a scribble drawn on a
   * phone came to 28KB at 600 — so the limit only ever refuses something that
   * is not a pen stroke.
   */
  maxSenderSignatureBytes: 32 * 1024,
  /**
   * How many places in its own text one document may ask to be signed.
   *
   * Enough for both parties and a witness. The cap is what keeps the request
   * body, and the rendered PDF, bounded by the document's structure rather than
   * by whoever fills it in.
   */
  maxDocumentSignatureFields: 4,
  /**
   * SIG-003 — the file a business uploads to be signed.
   *
   * Generous enough for a scanned multi-page contract and short of the size at
   * which rendering it in a phone browser stops being a good idea.
   */
  maxSignatureSourceBytes: 15 * 1024 * 1024,
  /** Pages past this are neither rendered for placement nor stamped. */
  maxSignaturePages: 40,
  /** How many places on one document may ask to be signed or filled in. */
  maxSignatureAnchors: 20,
  /**
   * SIG-003 — how many people one document may be sent to for signature.
   *
   * Four, because that is what the paperwork this replaces has room for: a
   * contract signed by both sides and witnessed, a delivery note signed by
   * whoever received it and whoever handed it over. Beyond that the thing being
   * managed is an approval chain, which is a different product.
   */
  maxSigners: 4,
  /**
   * SCAN — a stack of pages photographed one at a time and filed as one PDF.
   *
   * None of these is a limit on how long a scan may be. A scan is as long as
   * the person holding the phone says it is, and it ends when they say it has
   * ended — a cap that stops the camera mid-stack is the application telling
   * somebody to go and do the rest of their filing twice.
   *
   * They are limits on what one request may cost the server, which is a
   * different question with a different answer. `maxScanTotalBytes` is the one
   * that does that work: every page is held in memory at once to be merged, so
   * what matters is the total, and a hundred clean pages are lighter than four
   * unprocessed photographs. The page count is a backstop against a request
   * built to have a hundred thousand empty parts, set far past anything a
   * camera in a hand will reach.
   */
  maxScanPages: 500,
  maxScanPageBytes: 15 * 1024 * 1024,
  maxScanTotalBytes: 150 * 1024 * 1024,
  /** A filed document is a document, not a film of one. */
  maxLibraryFileBytes: 25 * 1024 * 1024,
} as const;

/**
 * SIG-001 — where a document to be signed stands.
 *
 * `viewed` is separate from `sent` because it is the only thing that
 * distinguishes "they have not got round to it" from "it never reached them",
 * and that decides whether the next move is a reminder or a phone call.
 */
/*
 * `declined` and `expired` are both endings the sender did not choose, and they
 * are not the same ending: a document that was refused was read and answered,
 * and one that expired was never answered at all. `cancelled` is the third — the
 * sender withdrawing the ask — and rolling any two of them together would lose
 * exactly the fact that decides what to do next.
 */
export const SIGNATURE_STATUSES = ['draft', 'sent', 'viewed', 'signed', 'declined', 'expired', 'cancelled'] as const;

/**
 * MKT-001 — the shapes a landing-page section can take.
 *
 * Four, and not one general block with a dozen switches: `plans` is the only
 * one that renders live data, `hero` and `cta` are the two ends of the page,
 * and everything in between is a `feature`. A section that needs a fifth shape
 * needs a design decision, not a new column.
 */
export const LANDING_SECTION_KINDS = ['hero', 'feature', 'plans', 'cta'] as const;

export { CURRENCIES, CURRENCY_SYMBOLS } from './currencies.js';
